import { Modal } from "@/components/ui/Modal/Modal";
import { ControllerInput } from "@/components/ui/Input/ControllerInput";
import { Select } from "@/components/ui/Select/Select";
import { MultiSelect, type SelectOption } from "@/components/ui/Select/MultiSelect";
import { TextArea } from "@/components/ui/TextArea/TextArea";
import { Alert, Button as OnEngineButton, ToggleItem } from "@onengine/component-library";
import { Group, Stack, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { FormProvider, useForm, Controller, useWatch, type Resolver } from "react-hook-form";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  FormBuilderQueryKeys,
  getFormBuilderDropdown,
  getFormBuilderList,
} from "@/api/form-builder/formBuilder.queries";
import {
  scopeTargetSchema,
  toFormTypeSlug,
  toWorkOrderTypeSlug,
  type ScopeAssignments,
  type ScopeTarget,
} from "@/components/features/FormBuilder/utils/scopeAssignments";
import {
  ALL_OPTION_VALUE,
  normalizeAllSelection,
  stripAllSelection,
  withAllOption,
} from "./allSelection";
import { formNameTakenMessage } from "@/api/form-builder/formNameTaken";
import { ScopeTargetsPicker } from "./ScopeTargetsPicker";
import {
  resolveSlotPrefill,
  stillMatchesSlot,
  type SlotIssue,
  type ResolvedSlotPrefill,
  type SlotPrefill,
} from "./slotPrefill";
import styles from "./NewFormTemplateModal.module.scss";

export interface NewFormTemplateValues {
  name: string;
  formTypeId: string;
  workOrderTypeId: string;
  tradeIds: string[];
  subTradeIds: string[];
  levelIds: string[];
  scopeTargets: ScopeTarget[];
  createAssignments: boolean;
  assignmentFormType?: ScopeAssignments["formType"];
  assignmentWorkOrderType?: ScopeAssignments["workOrderType"];
  description: string;
  /** Only meaningful when created via the ServEngine-scope super admin. */
  isGlobal: boolean;
  /** The form was created for a slot and still matches it, so publishing should assign it there. */
  assignToSlot?: boolean;
}

export type FormDetailsInitialValues = Pick<
  NewFormTemplateValues,
  | "name"
  | "formTypeId"
  | "workOrderTypeId"
  | "tradeIds"
  | "subTradeIds"
  | "levelIds"
  | "description"
>;

interface NewFormTemplateModalProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (values: NewFormTemplateValues) => void;
  /** Shows the "Available to all entities" checkbox — ServEngine-scope super admin only. */
  canFlagVisibility?: boolean;
  mode?: "create" | "edit";
  initialValues?: FormDetailsInitialValues;
  warnOnSlotChange?: boolean;
  isSubmitting?: boolean;
  /** A name the server refused as a duplicate. The inline error shows until the name is edited. */
  takenName?: string;
  /** Entity the form is created in; scopes the name check to the same list the form will join. */
  tenantId?: string;
  /** Opens the form pre-filled for one assignment slot. Create mode only. */
  slotPrefill?: SlotPrefill;
}

const NAME_CHECK_PAGE_SIZE = 25;
// A common word can match more forms than one page holds, so the exact name may sit further on.
const NAME_CHECK_MAX_PAGES = 8;

const sameName = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

// A new form is only created on its first save in the editor, where a refused name has no way
// back, so the clash is caught here. A failed lookup lets the form through: the server still decides.
// Only the entity's own forms count: names are unique per entity, and a platform form that
// happens to share the name does not stop this one.
async function isNameInUse(name: string, tenantId?: string) {
  try {
    for (let page = 1; page <= NAME_CHECK_MAX_PAGES; page += 1) {
      const { data, pagination } = await getFormBuilderList(
        { page, pageSize: NAME_CHECK_PAGE_SIZE, search: name, tenantId },
        new AbortController().signal,
      );
      if (
        data.some((form) => sameName(form.name, name) && (!tenantId || form.tenantId === tenantId))
      )
        return true;
      if (data.length < NAME_CHECK_PAGE_SIZE || (pagination && !pagination.hasNext)) return false;
    }
    return false;
  } catch {
    return false;
  }
}

const NOT_ASSIGNED_TAIL = "Assign it from the Assignments screen once it's published.";

const slotChangedWarning = (label: string) =>
  `These settings no longer match ${label}, so this form won't be assigned there automatically. ${NOT_ASSIGNED_TAIL}`;

const slotToggleOffWarning = (label: string) =>
  `"Create these assignments when I publish" is off, so this form won't be assigned to ${label}. ${NOT_ASSIGNED_TAIL}`;

const slotUnavailableWarning = (label: string, reason: SlotIssue) =>
  reason === "slot"
    ? `This slot has no matching Work Order Type or Visit Type here, so this form can't be assigned to ${label} automatically. ${NOT_ASSIGNED_TAIL}`
    : `The scope for ${label} couldn't be set up, so this form won't be assigned there automatically. ${NOT_ASSIGNED_TAIL}`;

const VISIT_TYPE_HELP =
  "Check-In: on arrival · Assessment: diagnose · Repair: the fix · Check-Out: when done";

const SUB_TRADE_DISABLED_HELP = "Pick a specific trade to choose sub-trades";

const LEVEL_LOAD_ERROR = "Couldn't load levels. Close and reopen this form to retry.";

const SLOT_CHANGE_WARNING =
  "Changing the visit type, work order type or trades means existing assignments of this form may no longer match their slots. Review them on the Assignments screen after saving.";

const schema = z.object({
  name: z
    .string()
    .min(1, "Form Name is required")
    .max(255, "Form Name must be 255 characters or fewer"),
  formTypeId: z.string().min(1, "Visit Type is required"),
  workOrderTypeId: z.string().min(1, "Work Order Type is required"),
  tradeIds: z.array(z.string()),
  subTradeIds: z.array(z.string()),
  levelIds: z.array(z.string()),
  entityIds: z.array(z.string()),
  clientKeys: z.array(z.string()),
  brandKeys: z.array(z.string()),
  scopeTargets: z.array(scopeTargetSchema),
  createAssignments: z.boolean(),
  description: z.string().max(1000, "Description must be 1000 characters or fewer"),
  isGlobal: z.boolean(),
});

type FormSchema = z.infer<typeof schema>;

const defaultValues: FormSchema = {
  name: "",
  formTypeId: "",
  workOrderTypeId: "",
  tradeIds: [ALL_OPTION_VALUE],
  subTradeIds: [ALL_OPTION_VALUE],
  levelIds: [],
  entityIds: [],
  clientKeys: [],
  brandKeys: [],
  scopeTargets: [],
  createAssignments: true,
  description: "",
  isGlobal: false,
};

const UNSUPPORTED_LEVEL = "location";

const hasOptionalValues = (initial: FormDetailsInitialValues) =>
  initial.tradeIds.length > 0 ||
  initial.subTradeIds.length > 0 ||
  initial.levelIds.length > 0 ||
  initial.description.trim().length > 0;

const toFormValues = (initial: FormDetailsInitialValues): FormSchema => ({
  ...defaultValues,
  name: initial.name,
  formTypeId: initial.formTypeId,
  workOrderTypeId: initial.workOrderTypeId,
  tradeIds: initial.tradeIds.length > 0 ? [...initial.tradeIds] : [ALL_OPTION_VALUE],
  subTradeIds: initial.subTradeIds.length > 0 ? [...initial.subTradeIds] : [ALL_OPTION_VALUE],
  levelIds: [...initial.levelIds],
  description: initial.description,
  createAssignments: false,
});

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && [...a].sort().join(",") === [...b].sort().join(",");

const changesSlot = (values: FormSchema, initial: FormDetailsInitialValues) =>
  values.formTypeId !== initial.formTypeId ||
  values.workOrderTypeId !== initial.workOrderTypeId ||
  !sameIds(stripAllSelection(values.tradeIds), initial.tradeIds) ||
  !sameIds(stripAllSelection(values.subTradeIds), initial.subTradeIds);

type DropdownItem = {
  id: string | number;
  label?: string;
  name?: string;
  value?: string;
};

const extractItems = (payload: unknown): DropdownItem[] => {
  if (!payload) return [];
  const data = (payload as { data?: unknown }).data;
  if (Array.isArray(data)) return data as DropdownItem[];
  if (Array.isArray((data as { data?: unknown })?.data)) {
    return (data as { data: DropdownItem[] }).data;
  }
  return Array.isArray(payload) ? (payload as DropdownItem[]) : [];
};

const toOptions = (
  items: DropdownItem[],
  getLabel: (item: DropdownItem) => string,
): SelectOption[] =>
  items.map((item) => ({
    value: String(item.id),
    label: getLabel(item),
  }));

const useFormBuilderModalDropdown = (key: string, enabled: boolean, filterValue?: string) =>
  useQuery({
    queryKey: [FormBuilderQueryKeys.dropdown, key, filterValue],
    queryFn: ({ signal }) =>
      getFormBuilderDropdown("", undefined, signal, key, {
        type: "",
        fields: [],
        limit: 100,
        key,
        filterValue,
      }),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

export function NewFormTemplateModal({
  opened,
  onClose,
  onSubmit,
  canFlagVisibility,
  mode = "create",
  initialValues,
  warnOnSlotChange = false,
  isSubmitting = false,
  takenName,
  tenantId,
  slotPrefill,
}: NewFormTemplateModalProps) {
  const isEdit = mode === "edit";
  const methods = useForm<FormSchema>({
    resolver: zodResolver(schema) as Resolver<FormSchema>,
    defaultValues,
    mode: "onTouched",
  });

  const { handleSubmit, reset, formState, control, setValue, getValues } = methods;
  const [confirmingSlotChange, setConfirmingSlotChange] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreId = useId();
  const visibilityId = useId();
  const actionsRef = useRef<HTMLDivElement>(null);

  const selectedTradeIds = useWatch({ control, name: "tradeIds" }) ?? [];
  const selectedSubTradeIds = useWatch({ control, name: "subTradeIds" }) ?? [];
  const specificTradeIds = stripAllSelection(selectedTradeIds);
  const tradeFilterValue = specificTradeIds.join(",");
  const hasTradesSelected = specificTradeIds.length > 0;

  const watchedName = useWatch({ control, name: "name" }) ?? "";
  const [checkedTakenName, setCheckedTakenName] = useState<string>();
  const refusedName = takenName ?? checkedTakenName;
  const nameTakenError =
    refusedName && sameName(watchedName, refusedName)
      ? formNameTakenMessage(refusedName.trim())
      : undefined;

  const hasHiddenFieldError = !!formState.errors.description;
  useEffect(() => {
    if (hasHiddenFieldError) setMoreOpen(true);
  }, [hasHiddenFieldError]);

  const selectedFormTypeId = useWatch({ control, name: "formTypeId" });
  const selectedWorkOrderTypeId = useWatch({ control, name: "workOrderTypeId" });
  const slotSelection = [
    selectedFormTypeId,
    selectedWorkOrderTypeId,
    tradeFilterValue,
    stripAllSelection(selectedSubTradeIds).join(","),
  ].join("|");

  useEffect(() => {
    setConfirmingSlotChange(false);
  }, [slotSelection]);

  useEffect(() => {
    if (!confirmingSlotChange) return;
    actionsRef.current?.querySelector<HTMLButtonElement>('button[type="submit"]')?.focus();
  }, [confirmingSlotChange]);

  const previousTradeFilterRef = useRef(tradeFilterValue);
  useEffect(() => {
    if (previousTradeFilterRef.current === tradeFilterValue) return;
    previousTradeFilterRef.current = tradeFilterValue;
    setValue("subTradeIds", [ALL_OPTION_VALUE], { shouldDirty: true, shouldValidate: false });
  }, [tradeFilterValue, setValue]);

  useEffect(() => {
    if (!opened || !isEdit || !initialValues) return;
    previousTradeFilterRef.current = initialValues.tradeIds.join(",");
    reset(toFormValues(initialValues));
    setMoreOpen(hasOptionalValues(initialValues));
    setConfirmingSlotChange(false);
  }, [opened, isEdit, initialValues, reset]);

  const handleAllAwareChange =
    (field: "tradeIds" | "subTradeIds", previous: string[]) => (next: string[]) =>
      setValue(field, normalizeAllSelection(next, previous), { shouldDirty: true });

  const formTypeQuery = useFormBuilderModalDropdown("formType", opened);
  const workOrderTypeQuery = useFormBuilderModalDropdown("workOrderType", opened);
  const tradeQuery = useFormBuilderModalDropdown("trade", opened);
  const subTradeQuery = useFormBuilderModalDropdown(
    "subTrade",
    opened && hasTradesSelected,
    tradeFilterValue || undefined,
  );
  const levelQuery = useFormBuilderModalDropdown("level", opened);
  const levelLoadError = levelQuery.isError ? LEVEL_LOAD_ERROR : undefined;

  const formTypeOptions = useMemo(
    () => toOptions(extractItems(formTypeQuery.data), (item) => item.label ?? String(item.id)),
    [formTypeQuery.data],
  );
  const workOrderTypeOptions = useMemo(
    () => toOptions(extractItems(workOrderTypeQuery.data), (item) => item.label ?? String(item.id)),
    [workOrderTypeQuery.data],
  );
  const tradeOptions = useMemo(
    () =>
      withAllOption(
        "All trades",
        toOptions(extractItems(tradeQuery.data), (item) => item.name ?? String(item.id)),
      ),
    [tradeQuery.data],
  );
  const subTradeOptions = useMemo(
    () =>
      withAllOption(
        "All sub-trades",
        toOptions(extractItems(subTradeQuery.data), (item) => item.name ?? String(item.id)),
      ),
    [subTradeQuery.data],
  );
  const levelOptions = useMemo(
    () =>
      toOptions(
        extractItems(levelQuery.data).filter(
          (item) => (item.label ?? item.value)?.trim().toLowerCase() !== UNSUPPORTED_LEVEL,
        ),
        (item) => item.label ?? String(item.id),
      ),
    [levelQuery.data],
  );

  const [resolvedSlot, setResolvedSlot] = useState<ResolvedSlotPrefill | null>(null);
  const [slotIssue, setSlotIssue] = useState<SlotIssue | null>(null);
  const appliedSlotRef = useRef<SlotPrefill | null>(null);
  const lookupsSettled = [formTypeQuery, workOrderTypeQuery, levelQuery].every(
    (query) => query.isSuccess || query.isError,
  );
  useEffect(() => {
    if (!opened || isEdit || !slotPrefill) {
      appliedSlotRef.current = null;
      setResolvedSlot(null);
      setSlotIssue(null);
      return;
    }
    // Applied once, after every dropdown has answered: a level list that arrives late would
    // otherwise be read as a scope that does not exist.
    if (appliedSlotRef.current === slotPrefill || !lookupsSettled) return;
    appliedSlotRef.current = slotPrefill;
    const resolved = resolveSlotPrefill(slotPrefill, {
      formTypeOptions,
      workOrderTypeOptions,
      levelOptions,
    });
    if (!resolved) {
      setSlotIssue("slot");
      setMoreOpen(true);
      return;
    }
    previousTradeFilterRef.current = resolved.tradeIds.join(",");
    // What the person typed while the dropdowns loaded stays.
    const typed = getValues();
    reset({
      ...defaultValues,
      ...resolved,
      name: typed.name || resolved.name,
      description: typed.description,
      tradeIds: resolved.tradeIds.length > 0 ? resolved.tradeIds : [ALL_OPTION_VALUE],
      subTradeIds: resolved.subTradeIds.length > 0 ? resolved.subTradeIds : [ALL_OPTION_VALUE],
    });
    setResolvedSlot(resolved);
    setSlotIssue(resolved.levelIds.length === 0 ? "scope" : null);
    setMoreOpen(true);
  }, [
    opened,
    isEdit,
    slotPrefill,
    lookupsSettled,
    formTypeOptions,
    workOrderTypeOptions,
    levelOptions,
    reset,
    getValues,
  ]);

  const watchedScopeTargets = useWatch({ control, name: "scopeTargets" }) ?? [];
  const watchedCreateAssignments = useWatch({ control, name: "createAssignments" });
  const slotStillMatches =
    !!slotPrefill &&
    !!resolvedSlot &&
    stillMatchesSlot(slotPrefill, resolvedSlot, {
      formTypeId: selectedFormTypeId,
      workOrderTypeId: selectedWorkOrderTypeId,
      tradeIds: specificTradeIds,
      subTradeIds: stripAllSelection(selectedSubTradeIds),
      scopeTargets: watchedScopeTargets,
    });
  const assignsToSlot = slotStillMatches && !!watchedCreateAssignments;
  // A form made for one entity's slot is that entity's own, never a global one.
  const showVisibilityToggle = !!canFlagVisibility && !isEdit && !slotPrefill;
  const assignsToEntities =
    showVisibilityToggle && !!watchedCreateAssignments && watchedScopeTargets.length > 0;

  const handleClose = () => {
    reset(defaultValues);
    setCheckedTakenName(undefined);
    setMoreOpen(false);
    setConfirmingSlotChange(false);
    onClose();
  };

  const submit = async (values: FormSchema) => {
    if (!isEdit && (await isNameInUse(values.name.trim(), tenantId))) {
      setCheckedTakenName(values.name);
      return;
    }
    if (
      isEdit &&
      warnOnSlotChange &&
      initialValues &&
      !confirmingSlotChange &&
      changesSlot(values, initialValues)
    ) {
      setConfirmingSlotChange(true);
      return;
    }
    const labelOf = (options: SelectOption[], id: string) =>
      options.find((option) => option.value === id)?.label;
    const scopeTargets = isEdit ? [] : values.scopeTargets;
    const mustBeGlobal =
      showVisibilityToggle && values.createAssignments && scopeTargets.length > 0;
    onSubmit({
      name: values.name.trim(),
      formTypeId: values.formTypeId,
      workOrderTypeId: values.workOrderTypeId,
      tradeIds: stripAllSelection(values.tradeIds),
      subTradeIds: stripAllSelection(values.subTradeIds),
      levelIds: [...values.levelIds],
      scopeTargets,
      createAssignments: values.createAssignments && scopeTargets.length > 0,
      assignmentFormType: toFormTypeSlug(labelOf(formTypeOptions, values.formTypeId)),
      assignmentWorkOrderType: toWorkOrderTypeSlug(
        labelOf(workOrderTypeOptions, values.workOrderTypeId),
      ),
      description: values.description.trim(),
      isGlobal: showVisibilityToggle ? values.isGlobal || mustBeGlobal : false,
      assignToSlot: !isEdit && assignsToSlot,
    });
    setConfirmingSlotChange(false);
    // Edit mode keeps the values: its caller closes the modal only once the save succeeds.
    if (!isEdit) {
      reset(defaultValues);
      setCheckedTakenName(undefined);
      setMoreOpen(false);
    }
  };

  const submitLabel = !isEdit
    ? "Create Form"
    : confirmingSlotChange
      ? "Confirm and Save"
      : "Save Changes";

  return (
    <Modal
      isOpen={opened}
      onClose={handleClose}
      title={isEdit ? "Edit Form Details" : "New Form Template"}
      size={640}
      contentClassName={styles.modalContent}
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(submit)} noValidate className={styles.form}>
          <Stack gap="md" className={styles.fields} data-testid="new-form-fields">
            <ControllerInput
              name="name"
              label="Form Name"
              placeholder="Enter…"
              size="small"
              error={nameTakenError}
              required
            />

            <Select
              name="workOrderTypeId"
              label="Work Order Type"
              placeholder="Select…"
              options={workOrderTypeOptions}
              loading={workOrderTypeQuery.isFetching}
              error={formState.errors.workOrderTypeId?.message}
              size="small"
              required
            />

            <Select
              name="formTypeId"
              label="Visit Type"
              placeholder="Select…"
              options={formTypeOptions}
              loading={formTypeQuery.isFetching}
              error={formState.errors.formTypeId?.message}
              size="small"
              required
              description={VISIT_TYPE_HELP}
              inputWrapperOrder={["label", "input", "description", "error"]}
              className={styles.visitType}
            />

            <div className={styles.moreOptions}>
              <button
                type="button"
                className={styles.moreToggle}
                aria-expanded={moreOpen}
                aria-controls={moreId}
                aria-describedby={`${moreId}-summary`}
                onClick={() => setMoreOpen((open) => !open)}
              >
                <IconChevronDown
                  size={16}
                  aria-hidden
                  className={clsx(styles.chevron, moreOpen && styles.chevronOpen)}
                />
                Assignment options (optional)
              </button>
              <p id={`${moreId}-summary`} className={styles.moreSummary} hidden={moreOpen}>
                Trade, sub-trade, where it applies and description
              </p>
              <div id={moreId} hidden={!moreOpen}>
                <Stack gap="md" className={styles.moreBody}>
                  <div className={styles.tradeGrid}>
                    <MultiSelect
                      name="tradeIds"
                      label="Trade"
                      placeholder="Select all that apply…"
                      options={tradeOptions}
                      loading={tradeQuery.isFetching}
                      error={
                        Array.isArray(formState.errors.tradeIds)
                          ? undefined
                          : formState.errors.tradeIds?.message
                      }
                      size="small"
                      showCheckboxes
                      optional
                      wrapperClassName={styles.tradeCell}
                      onChangeCallback={handleAllAwareChange("tradeIds", selectedTradeIds)}
                    />
                    <MultiSelect
                      name="subTradeIds"
                      label="Sub Trade"
                      placeholder="Select all that apply…"
                      options={subTradeOptions}
                      loading={subTradeQuery.isFetching}
                      disabled={!hasTradesSelected}
                      description={hasTradesSelected ? undefined : SUB_TRADE_DISABLED_HELP}
                      inputWrapperOrder={["label", "input", "description", "error"]}
                      optional
                      size="small"
                      showCheckboxes
                      wrapperClassName={styles.tradeCell}
                      onChangeCallback={handleAllAwareChange("subTradeIds", selectedSubTradeIds)}
                    />
                  </div>

                  {isEdit ? (
                    <MultiSelect
                      name="levelIds"
                      label="Applies to"
                      placeholder="Select all that apply…"
                      options={levelOptions}
                      loading={levelQuery.isFetching}
                      error={levelLoadError}
                      size="small"
                      showCheckboxes
                      optional
                    />
                  ) : (
                    <ScopeTargetsPicker
                      opened={opened}
                      levelOptions={levelOptions}
                      levelLoading={levelQuery.isFetching}
                      levelError={
                        levelLoadError ??
                        (Array.isArray(formState.errors.levelIds)
                          ? undefined
                          : formState.errors.levelIds?.message)
                      }
                      canPickEntities={!!canFlagVisibility && !slotPrefill}
                      lockedEntity={
                        slotPrefill
                          ? { id: slotPrefill.scope.tenantId, name: slotPrefill.entityName }
                          : undefined
                      }
                    />
                  )}

                  <Controller
                    name="description"
                    render={({ field, fieldState }) => (
                      <TextArea
                        label="Description"
                        placeholder="Enter description…"
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState.error?.message}
                        rows={4}
                        optional
                      />
                    )}
                  />
                </Stack>
              </div>
            </div>

            {showVisibilityToggle && (
              <Controller
                name="isGlobal"
                render={({ field }) => (
                  <Stack
                    gap={4}
                    className={styles.visibility}
                    role="group"
                    aria-labelledby={visibilityId}
                  >
                    <Text id={visibilityId} size="xs" fw={600} c="dimmed" tt="uppercase">
                      Visibility
                    </Text>
                    <ToggleItem
                      checked={field.value || assignsToEntities}
                      onChange={field.onChange}
                      disabled={assignsToEntities}
                      size="sm"
                      intent="brand"
                      primary="Available to all entities"
                    />
                    {assignsToEntities && (
                      <Text size="xs" c="dimmed">
                        Required to assign this form to the entities above.
                      </Text>
                    )}
                  </Stack>
                )}
              />
            )}

            {slotPrefill && (resolvedSlot || slotIssue) && (
              <p
                className={clsx(styles.slotNote, !assignsToSlot && styles.slotNoteWarning)}
                role="status"
              >
                {assignsToSlot
                  ? `Will be assigned to ${slotPrefill.label} when you publish.`
                  : slotIssue
                    ? slotUnavailableWarning(slotPrefill.label, slotIssue)
                    : slotStillMatches
                      ? slotToggleOffWarning(slotPrefill.label)
                      : slotChangedWarning(slotPrefill.label)}
              </p>
            )}

            {confirmingSlotChange && (
              <div role="alert">
                <Alert
                  title="Assignments may stop matching"
                  message={SLOT_CHANGE_WARNING}
                  type="warning"
                  variant="plain"
                  size="small"
                  presentation="inline"
                />
              </div>
            )}
          </Stack>
          <Group ref={actionsRef} justify="flex-end" gap="md" className={styles.actions}>
            <OnEngineButton variant="tertiary" size="small" onClick={handleClose}>
              Cancel
            </OnEngineButton>
            <OnEngineButton
              variant="primary"
              size="small"
              htmlAttributes={{ type: "submit" }}
              disabled={formState.isSubmitting || isSubmitting}
            >
              {submitLabel}
            </OnEngineButton>
          </Group>
        </form>
      </FormProvider>
    </Modal>
  );
}
